import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useSearchParams } from 'react-router-dom';
import { Plus, MessagesSquare } from 'lucide-react';
import { CommsTabs } from '../components/bulk/CommsTabs';
import { cn } from '@/lib/utils';
import { Button } from '../components/ui/button';
import { commsAPI, errMsg } from '../lib/communicationsAPI';
import { useCommsUnread } from '../hooks/useComms';
import { ConversationList } from '../components/comms/ConversationList';
import { BulkBar, FilterPopover, FilterChips } from '../components/comms/BulkBar';
import { ConversationDetail } from '../components/comms/ConversationDetail';
import { StartConversationDialog } from '../components/comms/StartConversationDialog';

const TABS = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'mine', label: 'Mine' },
  { key: 'waiting_client', label: 'Waiting on client' },
  { key: 'done', label: 'Done' },
  { key: 'unmatched', label: 'Unmatched' },
];

function useDebounced(v, ms) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

// Unified Inbox: 2 panels (list | conversation), 4 tabs, one search + one Filter popover.
export default function CommunicationsInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('inbox');
  const [search, setSearch] = useState('');
  const q = useDebounced(search, 300);
  const [filters, setFilters] = useState({});
  const [state, setState] = useState({ rows: [], loading: true, error: null, nextCursor: null, loadingMore: false });
  const [selected, setSelected] = useState(searchParams.get('c'));
  const [checked, setChecked] = useState(new Set());
  const [staff, setStaff] = useState([]);
  const [startOpen, setStartOpen] = useState(false);
  const [role, setRole] = useState(null);
  useEffect(() => { api.get('/api/me').then((r) => setRole(r.data.active_membership?.role)).catch(() => {}); }, []);
  useEffect(() => { if (searchParams.get('new') === '1') { setStartOpen(true); const n = new URLSearchParams(searchParams); n.delete('new'); setSearchParams(n, { replace: true }); } }, [searchParams]); // eslint-disable-line
  const counts = useCommsUnread().folders || {};

  useEffect(() => { if (searchParams.get('c')) { setSelected(searchParams.get('c')); setTab('all'); } }, [searchParams]);
  useEffect(() => { if (searchParams.get('new')) setStartOpen(true); }, [searchParams]);
  useEffect(() => { commsAPI.staff().then((r) => setStaff(r.data.staff)).catch(() => {}); }, []);
  const meId = (() => { try { return JSON.parse(sessionStorage.getItem('user'))?.id; } catch { return null; } })();

  const params = useMemo(() => {
    const p = { folder: tab, limit: 25, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== false && v != null)) };
    if (q.trim()) p.q = q.trim();
    return p;
  }, [tab, q, filters]);

  const load = useCallback(async (keep = false) => {
    setState((s) => ({ ...s, loading: !keep, error: null }));
    try {
      const res = await commsAPI.list({ ...params, limit: keep ? Math.min(100, Math.max(25, state.rows.length)) : 25 });
      setState({ rows: res.data.conversations, loading: false, error: null, nextCursor: res.data.next_cursor, loadingMore: false });
    } catch (err) { setState((s) => ({ ...s, loading: false, error: errMsg(err, 'Failed to load conversations') })); }
  }, [params, state.rows.length]);

  useEffect(() => { setChecked(new Set()); load(false); }, [params]); // eslint-disable-line
  useEffect(() => { const t = setInterval(() => load(true), 15000); return () => clearInterval(t); }, [load]);

  const loadMore = async () => {
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const res = await commsAPI.list({ ...params, cursor: state.nextCursor });
      setState((s) => ({ ...s, rows: [...s.rows, ...res.data.conversations], nextCursor: res.data.next_cursor, loadingMore: false }));
    } catch { setState((s) => ({ ...s, loadingMore: false })); }
  };
  const onCheck = (id, v) => setChecked((s) => { const n = new Set(s); if (v) n.add(id); else n.delete(id); return n; });

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="comms-inbox">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border">
        <MessagesSquare className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Communications</h1>
        {role && role !== 'read_only' && <Button size="sm" className="ml-auto" onClick={() => setStartOpen(true)} data-testid="comms-start-conversation-btn"><Plus className="w-4 h-4 mr-1.5" />New conversation</Button>}
      </div>
      <CommsTabs />
      <div className="flex items-center gap-1 px-3 border-b border-border overflow-x-auto" role="tablist" aria-label="Inbox views">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => { setTab(t.key); setSelected(null); }}
            data-testid={`comms-tab-${t.key}`}
            className={cn('px-3 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              tab === t.key ? 'border-primary text-foreground font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {t.label}
            {counts[t.key] > 0 && <span className="ml-1.5 text-[11px] font-semibold text-primary tabular-nums" data-testid={`comms-tab-count-${t.key}`}>{counts[t.key]}</span>}
          </button>
        ))}
      </div>
      <div className="flex-1 flex min-h-0">
        <div className={cn('min-h-0 flex', selected ? 'hidden md:flex' : 'flex w-full md:w-auto')}>
          <ConversationList state={state} selectedId={selected} onOpen={setSelected} search={search} onSearch={setSearch}
            checked={checked} onCheck={onCheck} onLoadMore={loadMore}
            toolbar={checked.size > 0
              ? <BulkBar ids={[...checked]} staff={staff} onDone={() => { setChecked(new Set()); load(true); }} onClear={() => setChecked(new Set())} />
              : <div className="space-y-1.5"><div className="flex flex-wrap items-center gap-1.5"><FilterPopover filters={filters} onChange={setFilters} staff={staff} />
                  <Button size="sm" variant={filters.unread ? 'default' : 'outline'} className="h-8" onClick={() => setFilters({ ...filters, unread: filters.unread ? '' : true })} aria-pressed={!!filters.unread} data-testid="comms-quick-unread">Unread</Button>
                  {meId && <Button size="sm" variant={filters.assigned_user_id === meId ? 'default' : 'outline'} className="h-8" onClick={() => setFilters({ ...filters, assigned_user_id: filters.assigned_user_id === meId ? '' : meId })} aria-pressed={filters.assigned_user_id === meId} data-testid="comms-quick-mine">Assigned to me</Button>}
                </div><FilterChips filters={filters} onChange={setFilters} staff={staff} /></div>} />
        </div>
        {selected ? (
          <ConversationDetail key={selected} id={selected} staff={staff} onBack={() => setSelected(null)} onChanged={() => load(true)} />
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center text-muted-foreground text-sm" data-testid="comms-detail-empty">Select a conversation to read it.</div>
        )}
      </div>
      <StartConversationDialog open={startOpen} onClose={() => setStartOpen(false)}
        defaultClientId={searchParams.get('new') ? searchParams.get('client') : undefined}
        defaultSubject={searchParams.get('new') ? searchParams.get('subject') : undefined}
        defaultBody={searchParams.get('new') ? searchParams.get('body') : undefined}
        onCreated={(id) => { setStartOpen(false); setTab('waiting_client'); setSelected(id); }} />
    </div>
  );
}
